/* Home modules: the campaign's film, the categories' picture, and the rails dragged with a mouse. Loaded by the
   sections that use it; as a module it runs once however many sections ask for it. */

/* The campaign's film (sections/hero.liquid) plays while the campaign is on screen and loops; one button stops it
   (WCAG 2.2.2). The picture paints before the film is fetched. Unrelated app resources do not hold up playback.
   With reduced motion or «save data» the picture stands and there is no button. A start the browser
   refuses (iOS Low Power Mode) is tried again on the next touch or click. */
const campaigns = new WeakMap();
function mountCampaign(hero) {
  if (campaigns.has(hero)) return;
  const films = [...hero.querySelectorAll('[data-film]')];
  const toggle = hero.querySelector('[data-film-toggle]');
  if (!films.length || !toggle) return;
  const events = new AbortController();
  const options = { signal: events.signal };
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const allowed = () => !motion.matches && !navigator.connection?.saveData;
  let inView = false;
  let stopped = false;
  const poster = hero.querySelector('picture img');
  const posterReady = poster ? poster.decode().catch(() => {}) : Promise.resolve();
  // Yield a paint after decoding the poster; slow tracking/app resources must not delay the campaign.
  const painted = posterReady.then(
    () => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve))),
  );
  // A film on a part of the campaign that is not shown (the second picture on a phone) is left alone.
  const play = async (manual = false) => {
    // An explicit play keeps its user gesture and need not wait for unrelated page resources.
    if (!manual) await painted;
    if (!events.signal.aborted && hero.isConnected && inView && !stopped && !document.hidden && allowed())
      films.forEach((film) => {
        if (!film.getClientRects().length) return;
        film
          .play()
          .then(() => {
            // A section can reconnect with the same video while this older play request is still pending.
            if (events.signal.aborted && campaigns.has(hero)) return;
            if (events.signal.aborted || !hero.isConnected || !inView || stopped || document.hidden || !allowed())
              film.pause();
          })
          .catch(() => {});
      });
  };
  const pause = () => films.forEach((film) => film.pause());
  const show = () => {
    const playing = films.some((film) => !film.paused);
    toggle.toggleAttribute('data-playing', playing);
    toggle.setAttribute('aria-label', playing ? toggle.dataset.stop : toggle.dataset.play);
  };
  films.forEach((film) => {
    film.addEventListener(
      'playing',
      () => {
        film.dataset.playing = '';
        show();
      },
      options,
    );
    film.addEventListener('pause', show, options);
  });
  const observer = new IntersectionObserver(([entry]) => {
    inView = entry.isIntersecting;
    return inView ? play() : pause();
  });
  observer.observe(hero);
  document.addEventListener('visibilitychange', () => (document.hidden ? pause() : play()), options);
  const preference = () => {
    toggle.hidden = !allowed();
    if (!allowed()) return pause();
    if (inView) return play();
  };
  motion.addEventListener('change', preference, options);
  navigator.connection?.addEventListener?.('change', preference, options);
  preference();
  document.addEventListener(
    'click',
    (event) => {
      if (event.target.closest('[data-film-toggle]') === toggle) {
        stopped = toggle.hasAttribute('data-playing');
        return stopped ? pause() : play(true);
      }
      if (films.every((film) => film.paused)) play(true);
    },
    options,
  );
  campaigns.set(hero, () => {
    events.abort();
    observer.disconnect();
    pause();
    toggle.hidden = true;
    campaigns.delete(hero);
  });
}
const campaignSections = (root) => [
  ...(root.matches?.('.home-hero') ? [root] : []),
  ...root.querySelectorAll('.home-hero'),
];
campaignSections(document).forEach(mountCampaign);
document.addEventListener('shopify:section:load', ({ target }) => campaignSections(target).forEach(mountCampaign));
document.addEventListener('shopify:section:unload', ({ target }) =>
  campaignSections(target).forEach((hero) => campaigns.get(hero)?.()),
);

/* Configured categories and pictures rotate quietly on desktop; hover or keyboard use takes over the list. */
class CategoryIndex extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.home-index-list');
    this.image = this.querySelector('.home-index-image');
    this.toggle = this.querySelector('[data-category-toggle]');
    this.links = [...this.list.querySelectorAll('a')];
    this.pictures = this.links.filter((link) => link.dataset.srcset);
    if (!this.links.length) return;
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.desktop = matchMedia('(min-width: 800px) and (hover: hover) and (pointer: fine)');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.stopped = false;
    this.hovered = false;
    this.visible = false;
    const refresh = () => this.schedule();
    this.desktop.addEventListener('change', refresh, options);
    this.motion.addEventListener('change', refresh, options);
    navigator.connection?.addEventListener('change', refresh, options);
    document.addEventListener('visibilitychange', refresh, options);
    this.list.addEventListener(
      'pointerenter',
      (event) => {
        if (event.pointerType === 'mouse') {
          this.hovered = true;
          this.schedule();
        }
      },
      options,
    );
    this.list.addEventListener(
      'pointerleave',
      () => {
        this.hovered = false;
        this.schedule();
      },
      options,
    );
    this.list.addEventListener(
      'pointerover',
      (event) => {
        const link = event.target.closest('a');
        if (event.pointerType === 'mouse' && this.links.includes(link)) this.select(link);
      },
      options,
    );
    this.list.addEventListener(
      'focusin',
      (event) => {
        const link = event.target.closest('a');
        if (this.links.includes(link)) this.select(link);
      },
      options,
    );
    this.list.addEventListener('focusout', () => queueMicrotask(refresh), options);
    this.toggle.addEventListener(
      'click',
      () => {
        this.stopped = !this.stopped;
        this.schedule();
      },
      options,
    );
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.visible = entry.isIntersecting && entry.intersectionRatio >= 0.25;
        this.schedule();
      },
      { threshold: 0.25 },
    );
    this.observer.observe(this);
    this.select(this.pictures[0] || this.links[0]);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.events?.abort();
    this.observer?.disconnect();
  }

  select(link) {
    this.active = link;
    this.links.forEach((item) => item.toggleAttribute('data-active', item === link));
    this.image.hidden = !link.dataset.srcset;
    if (link.dataset.srcset && this.image.srcset !== link.dataset.srcset) this.image.srcset = link.dataset.srcset;
    this.schedule();
  }

  schedule() {
    clearTimeout(this.timer);
    if (!this.isConnected) return;
    const eligible =
      this.desktop.matches && !this.motion.matches && !navigator.connection?.saveData && this.pictures.length > 1;
    this.toggle.hidden = !eligible || this.image.hidden;
    this.toggle.toggleAttribute('data-stopped', this.stopped);
    this.toggle.setAttribute('aria-label', this.stopped ? this.toggle.dataset.play : this.toggle.dataset.pause);
    if (
      !eligible ||
      this.stopped ||
      !this.visible ||
      document.hidden ||
      this.hovered ||
      this.list.contains(document.activeElement)
    )
      return;
    this.timer = setTimeout(
      () => this.select(this.pictures[(this.pictures.indexOf(this.active) + 1) % this.pictures.length]),
      5000,
    );
  }
}
customElements.define('category-index', CategoryIndex);

/* Rails: a mouse drags the cards sideways (a touch screen swipes natively); a drag is never read as a click. */
let drag = null;
document.addEventListener('pointerdown', (event) => {
  const rail = event.pointerType === 'mouse' && event.button === 0 && event.target.closest('[data-rail]');
  if (rail && !event.target.closest('button, .card-quick'))
    drag = { rail, x: event.clientX, left: rail.scrollLeft, moved: false };
});
document.addEventListener('pointermove', (event) => {
  if (!drag) return;
  const dx = event.clientX - drag.x;
  if (!drag.moved && Math.abs(dx) > 4) {
    drag.moved = true;
    drag.rail.classList.add('is-dragging');
  }
  if (drag.moved) drag.rail.scrollLeft = drag.left - dx;
});
document.addEventListener('pointerup', () => {
  if (!drag) return;
  drag.rail.classList.remove('is-dragging');
  if (drag.moved) document.addEventListener('click', (event) => event.preventDefault(), { capture: true, once: true });
  drag = null;
});
